import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { useChatStore } from "@/stores/chat-store";
import { useThreadStore } from "@/stores/thread-store";
import type { BranchRequest, MessageSaveRequest } from "@/types/thread";
import {
  branchThread,
  clearAllThreads,
  createThread,
  deleteThread,
  getThread,
  listThreads,
  saveMessages,
  updateThread,
} from "@/services/thread-service";

export function useThreadList(conceptId: string) {
  return useQuery({
    queryKey: ["chat-threads", conceptId],
    queryFn: () => listThreads(conceptId),
    enabled: !!conceptId,
  });
}

export function useThread(conceptId: string, threadId: string | null) {
  return useQuery({
    queryKey: ["chat-thread", conceptId, threadId],
    queryFn: () => getThread(conceptId, threadId!),
    enabled: !!conceptId && !!threadId,
  });
}

export function useThreadActions(conceptId: string) {
  const queryClient = useQueryClient();

  const createThreadMutation = useMutation({
    mutationFn: (title: string) => createThread(conceptId, { title }),
    onSuccess: (thread) => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
      useThreadStore.getState().setActiveThreadId(thread.id);
    },
  });

  const renameThreadMutation = useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      updateThread(conceptId, threadId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
    },
  });

  const removeThreadMutation = useMutation({
    mutationFn: (threadId: string) => deleteThread(conceptId, threadId),
    onSuccess: (_data, threadId) => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
      const { activeThreadId, setActiveThreadId } = useThreadStore.getState();
      if (activeThreadId === threadId) {
        setActiveThreadId(null);
        useChatStore.getState().clearChat();
      }
    },
  });

  const saveMessagesMutation = useMutation({
    mutationFn: ({
      threadId,
      data,
    }: {
      threadId: string;
      data: MessageSaveRequest;
    }) => saveMessages(conceptId, threadId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
    },
    onError: () => {
      toast.warning("Messages saved locally but failed to sync to server");
    },
  });

  const branchThreadMutation = useMutation({
    mutationFn: ({
      threadId,
      data,
    }: {
      threadId: string;
      data: BranchRequest;
    }) => branchThread(conceptId, threadId, data),
    onSuccess: (detail) => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
      useThreadStore.getState().setActiveThreadId(detail.id);
      useChatStore.getState().loadMessages(
        detail.messages.map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          sources: m.sources,
        })),
      );
      toast.success("Branched conversation");
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () => clearAllThreads(conceptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads", conceptId] });
      useThreadStore.getState().setActiveThreadId(null);
      useChatStore.getState().clearChat();
    },
  });

  return {
    createThread: createThreadMutation,
    renameThread: renameThreadMutation,
    removeThread: removeThreadMutation,
    clearAll: clearAllMutation,
    saveMessages: saveMessagesMutation,
    branchThread: branchThreadMutation,
  };
}
